import { motion } from 'framer-motion';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  text?: string;
  className?: string;
  /** When true, spinner is centered fixed over full screen */
  fullScreen?: boolean;
}

const sizeClasses = {
  sm: 'w-8 h-8',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
  xl: 'w-20 h-20',
};

export function LoadingSpinner({
  size = 'md',
  text = 'Loading...',
  className = '',
  fullScreen = false,
}: LoadingSpinnerProps) {
  const wrapperClass = fullScreen
    ? 'fixed inset-0 z-[1000] flex items-center justify-center'
    : 'flex flex-col items-center justify-center';

  return (
    <div className={`${wrapperClass} ${className}`}>
      <motion.div
        className={`${sizeClasses[size]} relative`}
        animate={{ rotate: 360 }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'linear',
        }}
      >
        <svg
          width='100%'
          height='100%'
          viewBox='0 0 151 151'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          className='w-full h-full'
        >
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M25.5274 124.902C53.1603 152.535 97.8014 152.638 125.229 125.21C152.656 97.7829 152.553 53.1418 124.921 25.509C97.2877 -2.12389 52.6466 -2.22661 25.2192 25.2008C-2.20819 52.6429 -2.10547 97.2693 25.5274 124.902ZM22.1962 128.233C51.6487 157.686 99.2688 157.833 128.56 128.542C157.836 99.2504 157.704 51.6303 128.252 22.1778C98.7846 -7.27478 51.1645 -7.40685 21.8733 21.8696C-7.41779 51.146 -7.27104 98.7808 22.1962 128.233Z'
            fill='url(#paint0_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M22.3587 93.1304L22.2707 93.0423C21.6984 91.4281 21.1994 89.7845 20.7885 88.1409L39.2642 69.6652C41.3921 69.9294 43.5346 69.944 45.6625 69.7239L23.3566 92.0298C23.0191 92.3673 22.6816 92.7488 22.3587 93.1304ZM64.2556 60.1559C64.1676 64.1181 62.5827 68.095 59.5156 71.1767L50.4905 80.2018C52.0608 80.6861 53.5869 81.3171 55.0691 82.0802L62.7441 74.4052C68.2178 68.9315 70.0522 61.2271 68.2032 54.418C67.1466 56.4431 65.8258 58.3655 64.2556 60.1559Z'
            fill='url(#paint1_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.2,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M128.221 57.3532L128.309 57.4413C128.881 59.0555 129.38 60.6991 129.791 62.3427L111.315 80.8331C109.187 80.5689 107.045 80.5543 104.917 80.7744L127.223 58.4685C127.56 58.1163 127.898 57.7348 128.221 57.3532ZM86.3238 90.3277C86.4119 86.3655 87.9968 82.3886 91.0638 79.3069L100.089 70.2818C98.5187 69.7975 96.9925 69.1665 95.5103 68.4034L87.8354 76.0784C82.3616 81.5521 80.5273 89.2565 82.3763 96.0656C83.4329 94.0405 84.7536 92.1181 86.3238 90.3277Z'
            fill='url(#paint2_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.4,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M35.3903 114.204L32.0591 117.55C15.6672 100.85 10.9419 77.0035 17.8391 56.1505C18.8223 57.8234 19.9816 59.4376 21.3317 60.9198C16.489 79.3222 21.1703 99.6909 35.3903 114.204ZM54.7465 132.108C56.4928 131.183 58.1511 130.068 59.7066 128.762C78.5932 134.206 99.7985 129.481 114.694 114.586L114.723 114.557L114.752 114.527L118.083 117.858L118.054 117.888L118.025 117.917C100.899 135.028 76.0985 139.768 54.7465 132.108ZM132.494 94.7602C139.744 73.5989 134.945 49.2239 118.069 32.3477L114.738 35.679C129.412 50.3539 134.196 71.1922 129.06 89.9028C130.396 91.4289 131.54 93.0579 132.494 94.7602ZM90.113 21.3709C91.6245 20.0355 93.2241 18.8762 94.9264 17.9076C73.7799 10.6876 49.4048 15.5156 32.514 32.4065C32.3966 32.5239 32.2792 32.6559 32.1471 32.7733L35.4783 36.1045C35.5957 35.9871 35.7131 35.8551 35.8452 35.7377C50.5641 21.0334 71.4172 16.2494 90.113 21.3709Z'
            fill='url(#paint3_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.6,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M48.0661 47.7561C51.412 44.4102 51.412 38.9805 48.0661 35.6346C44.7202 32.2887 39.2905 32.2887 35.9446 35.6346C32.5987 38.9805 32.5987 44.4102 35.9446 47.7561C39.2905 51.102 44.7202 51.102 48.0661 47.7561ZM51.3973 51.0873C56.5775 45.907 56.5775 37.4983 51.3973 32.3034C46.217 27.1232 37.8083 27.1232 32.6134 32.3034C27.4331 37.4836 27.4331 45.8924 32.6134 51.0873C37.8083 56.2675 46.217 56.2675 51.3973 51.0873Z'
            fill='url(#paint4_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.8,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M56.7864 22.4577L56.8745 22.3696C58.4887 21.7973 60.1323 21.2983 61.7759 20.8875L80.2516 39.3632C79.9875 41.491 79.9728 43.6336 80.1929 45.7614L57.8871 23.4556C57.5349 23.118 57.168 22.7805 56.7864 22.4577ZM89.761 64.3545C85.7987 64.2665 81.8218 62.6816 78.7401 59.6145L69.715 50.5748C69.2308 52.145 68.5997 53.6712 67.8366 55.1534L75.5116 62.8283C80.9854 68.3021 88.6897 70.1365 95.4989 68.2874C93.4737 67.2455 91.5366 65.9248 89.761 64.3545Z'
            fill='url(#paint5_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.0,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M13.6837 40.3153C13.3462 46.0385 14.7257 51.8498 17.8074 56.9126L17.8221 56.8686C18.82 58.5269 19.9939 60.0971 21.344 61.5646C21.5788 61.8141 21.799 62.0489 22.0484 62.2837C33.2454 73.4806 51.4129 73.4806 62.6246 62.2837C66.0145 58.8938 68.3771 54.8728 69.7125 50.5878L65.853 46.7283C65.809 46.9631 65.7503 47.1832 65.6916 47.418C64.7084 51.6444 62.5805 55.6506 59.2934 58.9525C49.9308 68.3151 34.7569 68.3151 25.3943 58.9525C16.5307 50.0888 16.0611 36.0009 23.9855 26.5796C24.2643 26.2421 24.5578 25.9046 24.8513 25.5817C20.3902 30.0869 16.6627 35.047 13.6837 40.3153ZM21.9457 21.8103C22.1218 21.6342 22.2979 21.4727 22.4593 21.2966C22.3126 21.4287 22.1805 21.5755 22.0338 21.7075C22.0044 21.7516 21.9751 21.7809 21.9457 21.8103Z'
            fill='url(#paint6_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.2,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M102.571 102.465C99.2251 105.811 99.2251 111.241 102.571 114.587C105.917 117.933 111.347 117.933 114.692 114.587C118.038 111.241 118.038 105.811 114.692 102.465C111.347 99.1196 105.917 99.1196 102.571 102.465ZM99.2398 99.1342C94.0596 104.314 94.0596 112.723 99.2398 117.918C104.42 123.098 112.829 123.098 118.024 117.918C123.204 112.738 123.204 104.329 118.024 99.1342C112.829 93.9393 104.42 93.9393 99.2398 99.1342Z'
            fill='url(#paint7_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.4,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M93.7961 127.706L93.7081 127.794C92.0938 128.366 90.4502 128.865 88.8066 129.276L69.964 110.433C70.1988 108.276 70.1988 106.104 69.9494 103.947L92.6955 126.693C93.0477 127.045 93.4146 127.383 93.7961 127.706ZM60.396 85.8089C64.505 85.7796 68.658 87.3792 71.8424 90.5489L80.8675 99.574C81.3518 98.0038 81.9828 96.4776 82.7459 94.9954L75.0709 87.3205C69.5091 81.7587 61.658 79.9537 54.7608 81.9494C56.7566 82.9767 58.6497 84.2681 60.396 85.8089Z'
            fill='url(#paint8_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.6,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M136.837 110.652C137.365 104.665 136.001 98.5451 132.772 93.2474L132.757 93.2915C131.759 91.6332 130.586 90.063 129.235 88.5955C129.015 88.346 128.78 88.1112 128.531 87.8764C117.334 76.6795 99.1665 76.6795 87.9549 87.8764C84.565 91.2663 82.2023 95.2872 80.8669 99.5723L84.6971 103.402C85.152 101.245 85.9004 99.1321 86.957 97.1363C88.0576 95.0084 89.5104 93.0126 91.3008 91.2223C100.663 81.8597 115.837 81.8597 125.2 91.2223C132.258 98.2809 133.99 108.656 130.395 117.358C129.235 120.279 127.474 123.008 125.112 125.356C124.877 125.591 124.642 125.811 124.407 126.046C124.231 126.354 124.011 126.677 123.776 127.014C124.437 126.398 125.082 125.782 125.728 125.136C130.145 120.69 133.843 115.817 136.837 110.652ZM111.508 136.083C111.053 136.318 110.584 136.553 110.114 136.788C110.173 136.788 110.231 136.773 110.305 136.773C110.701 136.538 111.112 136.318 111.508 136.083Z'
            fill='url(#paint9_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1.8,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M103.187 48.3872C106.533 51.7331 111.963 51.7331 115.309 48.3872C118.655 45.0413 118.655 39.6116 115.309 36.2657C111.963 32.9199 106.533 32.9199 103.187 36.2657C99.8416 39.6116 99.8416 45.0267 103.187 48.3872ZM99.8563 51.7037C105.037 56.884 113.445 56.884 118.64 51.7037C123.82 46.5235 123.82 38.1148 118.64 32.9199C113.46 27.7396 105.051 27.7396 99.8563 32.9199C94.6761 38.1148 94.6761 46.5235 99.8563 51.7037Z'
            fill='url(#paint10_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2.0,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M110.362 14.2537C104.639 13.9162 98.8276 15.2956 93.7648 18.3773L93.8088 18.392C92.1505 19.3899 90.5803 20.5639 89.1128 21.914C88.8634 22.1488 88.6285 22.3689 88.3938 22.6184C77.1968 33.8154 77.1968 51.9829 88.3938 63.1945C91.7837 66.5844 95.8046 68.9471 100.09 70.2825L103.949 66.423C103.714 66.379 103.494 66.3203 103.259 66.2616C99.0331 65.2784 95.0268 63.1505 91.725 59.8633C82.3624 50.5007 82.3624 35.3269 91.725 25.9643C100.589 17.1006 114.677 16.631 124.098 24.5555C124.435 24.8343 124.773 25.1278 125.096 25.4213C120.591 20.9601 115.63 17.2327 110.362 14.2537Z'
            fill='url(#paint11_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2.2,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M47.4537 102.053C44.1078 98.7072 38.6781 98.7072 35.3322 102.053C31.9864 105.399 31.9864 110.829 35.3322 114.175C38.6781 117.52 44.1078 117.52 47.4537 114.175C50.7996 110.814 50.7996 105.399 47.4537 102.053ZM50.7849 98.7219C45.6047 93.5416 37.196 93.5416 32.001 98.7219C26.8208 103.902 26.8208 112.311 32.001 117.506C37.1813 122.686 45.59 122.686 50.7849 117.506C55.9652 112.311 55.9652 103.902 50.7849 98.7219Z'
            fill='url(#paint12_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2.4,
            }}
          />
          <motion.path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M40.217 136.231C45.9402 136.569 51.7515 135.19 56.8143 132.108L56.7703 132.093C58.4285 131.095 59.9988 129.921 61.4662 128.571C61.7157 128.336 61.9505 128.116 62.1853 127.867C73.3823 116.655 73.3823 98.5023 62.1853 87.2906C58.7954 83.9007 54.7745 81.5381 50.4894 80.2026L46.6299 84.0621C46.8647 84.1062 47.0848 84.1649 47.3196 84.2236C51.546 85.2068 55.5523 87.3346 58.8541 90.6218C68.2167 99.9844 68.2167 115.158 58.8541 124.521C49.9905 133.385 35.9025 133.854 26.4813 125.93C26.1437 125.651 25.8062 125.357 25.4834 125.064C29.9886 129.525 34.9487 133.252 40.217 136.231ZM21.7119 127.969C21.5358 127.793 21.3744 127.617 21.1983 127.456C21.3304 127.603 21.4771 127.735 21.6092 127.881C21.6532 127.911 21.6826 127.94 21.7119 127.969Z'
            fill='url(#paint13_linear_1763_1700)'
            animate={{
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 2.6,
            }}
          />
          <defs>
            <linearGradient
              id='paint0_linear_1763_1700'
              x1='75'
              y1='-4.00007e-08'
              x2='77.5'
              y2='150'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint1_linear_1763_1700'
              x1='55'
              y1='82'
              x2='56'
              y2='23'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint2_linear_1763_1700'
              x1='92'
              y1='129'
              x2='101'
              y2='68.9999'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint3_linear_1763_1700'
              x1='75'
              y1='0.5'
              x2='76.5'
              y2='149.5'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint4_linear_1763_1700'
              x1='32'
              y1='26'
              x2='61.5'
              y2='61'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint5_linear_1763_1700'
              x1='129.5'
              y1='57'
              x2='67.5'
              y2='52'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint6_linear_1763_1700'
              x1='3.00005'
              y1='24.5'
              x2='70'
              y2='49.5'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint7_linear_1763_1700'
              x1='85'
              y1='96.5'
              x2='123.5'
              y2='115'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint8_linear_1763_1700'
              x1='83.5'
              y1='97.4999'
              x2='20.5'
              y2='92.4999'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint9_linear_1763_1700'
              x1='88.5'
              y1='91.5001'
              x2='133'
              y2='135.5'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint10_linear_1763_1700'
              x1='118'
              y1='26'
              x2='98.5'
              y2='69'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint11_linear_1763_1700'
              x1='117.5'
              y1='12.0001'
              x2='100'
              y2='69.0001'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint12_linear_1763_1700'
              x1='50'
              y1='83'
              x2='35'
              y2='123'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
            <linearGradient
              id='paint13_linear_1763_1700'
              x1='52'
              y1='82'
              x2='35'
              y2='138'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#40C8B8' />
              <stop offset='0.5' stopColor='#31A3F5' />
              <stop offset='1' stopColor='#476BF7' />
            </linearGradient>
          </defs>
        </svg>
      </motion.div>
      {text && (
        <motion.p
          className='text-gray-600 mt-4 text-sm font-medium'
          animate={{
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
}
